import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, FileText, X } from 'lucide-react';
import { api, type ProjectTreeNode } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ancestorPathsOf, useProjectTree } from '../../lib/project-tree';
import { cn } from '../../lib/utils';
import { FileDropzone } from '../FileDropzone';
import { ProjectMark } from '../project/ProjectMark';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Input } from '../ui/input';
import { RowMenu } from '../ui/row-menu';

const EXPANDED_KEY = 'sidebar.projects.expanded';

/** Same shorthand the project overview's file list uses, so one file reads the same in both. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Derives a URL-safe slug from a display name, matching the server's `^[a-z0-9-]+$` rule. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-');
}

/**
 * One project in the hierarchy. Expands to its sub-projects and nothing else: a project's
 * knowledge-base pages are a view inside it (KbTreePanel), not part of the navigation between
 * projects, so this row offers the way *in* — the **Knowledge base** menu item — rather than the
 * pages themselves.
 */
function TreeRow({
  node, activeSlug, expanded, onToggle, onSelect, onAddChild, onOpenKb, onOpenSettings, canOpenKb, depth,
}: {
  node: ProjectTreeNode;
  activeSlug: string | null;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onSelect: (slug: string) => void;
  onAddChild: (parent: ProjectTreeNode) => void;
  onOpenKb: (slug: string) => void;
  onOpenSettings: (slug: string) => void;
  /** Whether the KB section is enabled for this user — the flag check the nav group used to do. */
  canOpenKb: boolean;
  depth: number;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.path);
  const isActive = node.slug === activeSlug;

  return (
    <>
      <div
        className={cn(
          'group/row flex items-center rounded-md transition-colors',
          isActive ? 'bg-orange-500 text-white' : 'hover:bg-surface-subtle',
        )}
        style={{ paddingLeft: depth * 12 }}
      >
        <button
          onClick={() => hasChildren && onToggle(node.path)}
          className={cn(
            'w-4 h-6 flex items-center justify-center flex-shrink-0',
            hasChildren ? (isActive ? 'text-orange-100' : 'text-ink-faint hover:text-ink') : 'opacity-0 cursor-default',
          )}
          tabIndex={hasChildren ? 0 : -1}
          aria-label={hasChildren ? (isOpen ? 'Collapse' : 'Expand') : undefined}
        >
          {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <button onClick={() => onSelect(node.slug)} className="flex-1 flex items-center gap-1.5 py-1.5 min-w-0 text-left">
          <ProjectMark project={node} active={isActive} />
          <span className={cn('text-xs truncate', isActive ? 'text-white font-medium' : 'text-ink')}>{node.name}</span>
        </button>
        <RowMenu
          label={`Actions for ${node.name}`}
          tone={isActive ? 'active' : 'default'}
          items={[
            { label: 'New sub-project', onClick: () => onAddChild(node) },
            // Both navigate to *this* row's project, which is the point of moving them off the
            // global nav: there they always meant "the project you happen to be in". Creating a page
            // is not offered here — it is done inside the knowledge base, where the page tree is.
            ...(canOpenKb
              ? [{ label: 'Knowledge base', onClick: () => onOpenKb(node.slug), separated: true }]
              : []),
            { label: 'Settings', onClick: () => onOpenSettings(node.slug), separated: !canOpenKb },
          ]}
        />
      </div>
      {isOpen &&
        node.children.map((child) => (
          <TreeRow
            key={child.id}
            node={child}
            activeSlug={activeSlug}
            expanded={expanded}
            onToggle={onToggle}
            onSelect={onSelect}
            onAddChild={onAddChild}
            onOpenKb={onOpenKb}
            onOpenSettings={onOpenSettings}
            canOpenKb={canOpenKb}
            depth={depth + 1}
          />
        ))}
    </>
  );
}

/**
 * The Organization → Project → Sub-project tree, and the sidebar's primary navigation.
 *
 * This replaced the flat workspace dropdown: with sub-projects, "which project am I in" is a
 * question about position in a tree, and a list of names can't answer it. Expansion is persisted
 * to localStorage — this is the top-level nav, and having it collapse on every reload would hide
 * most of the application.
 *
 * Projects only. For a while each project's knowledge-base pages were nested beneath its row,
 * which put a client/project hierarchy and the structure of one project's documents in one list;
 * producers navigate by client and project and should not have to reason about page structure to
 * do it, so the pages moved back inside the project (see KbTreePanel).
 */
export function ProjectNavTree({ activeSlug }: { activeSlug: string | null }) {
  const navigate = useNavigate();
  const { tree, allProjects, loading, error, reload } = useProjectTree();
  const { hasFeature } = useAuth();

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(EXPANDED_KEY);
      return new Set<string>(stored ? (JSON.parse(stored) as string[]) : []);
    } catch {
      return new Set<string>();
    }
  });

  const persist = useCallback((next: Set<string>) => {
    setExpanded(next);
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify([...next]));
    } catch {
      // A full or unavailable localStorage costs the memory of which branches were open,
      // which is not worth breaking navigation over.
    }
  }, []);

  const activeProject = useMemo(
    () => allProjects.find((p) => p.slug === activeSlug) ?? null,
    [allProjects, activeSlug],
  );
  const activePath = activeProject?.path ?? null;

  // Opening the branch that leads to the current project, so navigating straight to a deep URL
  // shows where you are rather than a collapsed root.
  useEffect(() => {
    if (!activePath) return;
    setExpanded((prev) => {
      const missing = ancestorPathsOf(activePath).filter((p) => !prev.has(p));
      if (missing.length === 0) return prev;
      return new Set([...prev, ...missing]);
    });
  }, [activePath]);

  // Roots are always open: with one organization, a collapsed root is an empty sidebar.
  useEffect(() => {
    if (tree.length === 0) return;
    setExpanded((prev) => {
      const missing = tree.map((r) => r.path).filter((p) => !prev.has(p));
      return missing.length === 0 ? prev : new Set([...prev, ...missing]);
    });
  }, [tree]);

  const [creatingUnder, setCreatingUnder] = useState<ProjectTreeNode | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  /** Files chosen before the project exists, queued into it the moment it does. */
  const [seedFiles, setSeedFiles] = useState<File[]>([]);
  /** "Uploading 2 of 5" — the create button's own progress, since it is doing two jobs. */
  const [seedProgress, setSeedProgress] = useState<{ done: number; total: number } | null>(null);

  const openCreate = (parent: ProjectTreeNode) => {
    setCreatingUnder(parent);
    setName('');
    setSlug('');
    setSlugEdited(false);
    setCreateError(null);
    setSeedFiles([]);
    setSeedProgress(null);
  };

  /**
   * Creates the project, then queues whatever was dropped into it.
   *
   * In that order necessarily — a file needs a project to be filed into — which is also why a
   * failure to create aborts before anything is uploaded, while a failure to upload does **not** undo
   * the project. By then the project genuinely exists and is fine; the file is the part that did not
   * land, and deleting a project someone just made because one PDF was unreadable would be a much
   * worse answer than saying so.
   *
   * Only the POSTs are awaited, not the ingestion. Each returns as soon as the run is queued, so this
   * is a few seconds for a handful of files rather than the minute-per-file the agent then spends —
   * and the project page picks those runs up live when it opens.
   */
  const submitCreate = async () => {
    if (!creatingUnder || !name.trim() || !slug.trim()) return;
    setSubmitting(true);
    setCreateError(null);

    let created: Awaited<ReturnType<typeof api.projects.create>>;
    try {
      created = await api.projects.create({ name: name.trim(), slug: slug.trim(), parentId: creatingUnder.id });
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
      return;
    }

    const failed: string[] = [];
    if (seedFiles.length > 0) {
      setSeedProgress({ done: 0, total: seedFiles.length });
      // Sequential, so the runs queue in the order they were dropped — the worker takes one project's
      // runs one at a time anyway, so this is what makes the visible order the real one.
      for (const [index, file] of seedFiles.entries()) {
        try {
          await api.kb.ingestFile(created.id, file, { source: file.name });
        } catch {
          failed.push(file.name);
        }
        setSeedProgress({ done: index + 1, total: seedFiles.length });
      }
    }

    // Reveal the new child rather than leaving it hidden inside a collapsed parent.
    persist(new Set([...expanded, creatingUnder.path]));
    reload();
    setCreatingUnder(null);
    setSubmitting(false);
    setSeedProgress(null);

    // `?start=1`: a project with nothing in it can do nothing — it has no pages, so no description,
    // so the router will never send it anything and it cannot fill itself. Landing with the "add
    // something" panel open is the one next step worth taking, and it adopts the runs just queued.
    const failedParam = failed.length > 0 ? `&failed=${encodeURIComponent(failed.join(','))}` : '';
    navigate(`/p/${created.slug}?start=1${failedParam}`);
  };

  const toggle = (key: string) => {
    const next = new Set(expanded);
    if (!next.delete(key)) next.add(key);
    persist(next);
  };

  return (
    <div className="px-2">
      <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">Projects</div>

      {loading && <p className="px-2 py-1 text-xs text-ink-faint">Loading…</p>}
      {error && <p className="px-2 py-1 text-xs text-red-500">{error}</p>}
      {!loading && !error && tree.length === 0 && (
        <p className="px-2 py-1 text-xs text-ink-faint">No organization yet.</p>
      )}

      {tree.map((root) => (
        <TreeRow
          key={root.id}
          node={root}
          activeSlug={activeSlug}
          expanded={expanded}
          onToggle={toggle}
          onSelect={(s) => navigate(`/p/${s}`)}
          onAddChild={openCreate}
          onOpenKb={(s) => navigate(`/p/${s}/kb`)}
          onOpenSettings={(s) => navigate(`/p/${s}/settings`)}
          canOpenKb={hasFeature('KB')}
          depth={0}
        />
      ))}

      {/* Portalled to the body: the sidebar is `fixed` and z-indexed, so a dialog rendered
          inside it is trapped in that stacking context and clipped by the rail. */}
      {creatingUnder &&
        createPortal(
          <Dialog open onClose={() => setCreatingUnder(null)} title={`New project under ${creatingUnder.name}`}>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">Name</label>
                <Input
                  autoFocus
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!slugEdited) setSlug(slugify(e.target.value));
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && submitCreate()}
                  placeholder="Authentication"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">Slug</label>
                <Input
                  value={slug}
                  onChange={(e) => { setSlugEdited(true); setSlug(slugify(e.target.value)); }}
                  onKeyDown={(e) => e.key === 'Enter' && submitCreate()}
                  placeholder="authentication"
                />
                <p className="mt-1 text-[11px] text-ink-faint">
                  Used in the URL, and unique across every project — not just among siblings.
                </p>
              </div>

              {/* Optional, but the difference between a project that works and one that cannot: with
                  no pages it has no description, so the router will never file anything here and it
                  cannot fill itself. One document is enough to break that. */}
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">
                  Start it with something <span className="text-ink-faint font-normal">(optional)</span>
                </label>
                <FileDropzone
                  onFilesAdded={(files) => setSeedFiles((prev) => [...prev, ...files])}
                  disabled={submitting}
                  label="Drop files here, or browse"
                  helperText="PDF, Word, Markdown or plain text."
                />

                {seedFiles.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {seedFiles.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center gap-2 text-[11px] text-ink-secondary"
                      >
                        <FileText size={11} className="text-ink-faint flex-shrink-0" />
                        <span className="truncate flex-1">{file.name}</span>
                        <span className="text-ink-faint flex-shrink-0">{formatBytes(file.size)}</span>
                        <button
                          onClick={() => setSeedFiles((prev) => prev.filter((_, i) => i !== index))}
                          disabled={submitting}
                          title="Remove"
                          className="text-ink-faint hover:text-danger-text transition-colors disabled:opacity-40"
                        >
                          <X size={11} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <p className="mt-1.5 text-[11px] text-ink-faint leading-snug">
                  Absorbed one at a time once the project exists — about a minute each. You can watch
                  them on the project page, and add more there.
                </p>
              </div>

              {createError && (
                <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-1.5">
                  {createError}
                </p>
              )}
              <div className="flex items-center justify-end gap-2">
                {seedProgress && (
                  <span className="mr-auto text-[11px] text-ink-muted">
                    Uploading {seedProgress.done} of {seedProgress.total}…
                  </span>
                )}
                <Button onClick={() => setCreatingUnder(null)} disabled={submitting}>Cancel</Button>
                <Button variant="primary" onClick={submitCreate} disabled={submitting || !name.trim() || !slug.trim()}>
                  {submitting
                    ? seedProgress
                      ? 'Uploading…'
                      : 'Creating…'
                    : seedFiles.length > 0
                      ? `Create and add ${seedFiles.length} file${seedFiles.length === 1 ? '' : 's'}`
                      : 'Create'}
                </Button>
              </div>
            </div>
          </Dialog>,
          document.body,
        )}
    </div>
  );
}
